import { ReportItem } from '../app/Interfaces/admin/Role.interfaces';

export const ROLE_TYPE_OPTIONS = [
  { label: 'Internal', value: 'internal' },
  { label: 'External', value: 'external' },
];

export const ACCOUNTING_SECTION: Record<string, ReportItem[]> = {
  'cash management': [{ label: 'cash' }],
  'accounts payables': [
    { label: 'Open Payables', value: 'openPayables' },
    { label: 'Aged Payables', value: 'agedPayables' },
    { label: 'Payment History', value: 'paymentHistory' },
    { label: 'Unapproved Invoices', value: 'unapprovedInvoices' },
    { label: 'Invoice Archive', value: 'invoiceArchive' },
    { label: 'Vendor List', value: 'vendorList' },
    { label: 'Open Payables Per Job', value: 'openPayablesPerJob' },
  ],
  'accounts receivables': [
    { label: 'Aged Analysis', value: 'agedAnalysis' },
    { label: 'Contract Billings and Receipts', value: 'contractBAR' },
    { label: 'Receipts History', value: 'receiptsHistory' },
    { label: 'Customer List', value: 'customerList' },
  ],
  'contract billings': [
    { label: 'Contract List', value: 'contractList' },
    {
      label: 'Contract Progress Worksheet',
      value: 'contractProgress',
    },
    { label: 'Monthly Billing', value: 'monthlyBilling' },
  ],
  'jobs & contracts': [
    { label: 'Contract Analysis', value: 'contractAnalysis' },
    { label: 'Cost/Estimate', value: 'costEstimate' },
    {
      label: 'Projected JC Report',
      value: 'projectedJC',
    },
    { label: 'Cost Detail', value: 'costDetail' },
    { label: 'Cost and Revenue', value: 'costRevenue' },
    { label: 'Contract Items with CO Detail', value: 'itemDetails' },
    { label: 'MTD/YTD/JTD Costs', value: 'costs' },
    { label: 'Job Phases', value: 'jobPhases' },
    { label: 'Phase Master', value: 'phaseMaster' },
    { label: 'Job Setup Info', value: 'jobSetupInfo' },
  ],
  payroll: [
    { label: 'Employee List', value: 'employee' },
    { label: 'Employee Earnings', value: 'earnings', blockAdmin: true },
  ],
  subcontracts: [
    { label: 'Last Payment Summary', value: 'paymentSummary' },
    { label: 'Subcontract Status Report', value: 'subcontractPayments' },
    {
      label: 'Subcontract Payment Worksheet',
      value: 'paymentWorksheet',
    },
  ],
  'financial statements': [
    {
      label: 'Financial Statements',
    },
  ],
  new: [
    { label: 'New Contract Setup' },
    { label: 'Schedule of Values' },
    { label: 'Job Phase Codes' },
    { label: 'Estimated Cost' },
    { label: 'PO Entry' },
    { label: 'Change Orders' },
    { label: 'SL Change Order' },
    { label: 'New Subcontract Setup' },
    { label: 'Document Drop Off' },
    { label: 'New PR Timesheet' },
    { label: 'Bonus' },
    { label: 'Petty Cash Reimbursement' },
    { label: 'Create Payment Batch' },
    { label: 'AP Transaction Entry' },
  ],
};

export const INSURANCE_SECTION: Record<string, ReportItem[]> = {
  policy: [{ label: 'policy' }],
  claim: [{ label: 'claim' }],
};

export const SYSTEM_MODULES = {
  accounting: ACCOUNTING_SECTION,
  insurance: INSURANCE_SECTION,
};

const getSectionReports = (
  section: string,
  module?: keyof typeof SYSTEM_MODULES
) => {
  const sectionReports = SYSTEM_MODULES[module ?? 'accounting'][section];

  if (!sectionReports) {
    return [];
  }

  const reports = sectionReports.map((report) =>
    report.label.replace(/ /g, '_').toLocaleLowerCase()
  );

  return reports;
};

export enum ProcessVisibilityRule {
  REPORT = 'report',
  ADMIN = 'admin',
}

export type ProcessOption = {
  label: string;
  value: string;
  visibilityRule: ProcessVisibilityRule;
};

const toProcessOption = (item: ReportItem): ProcessOption => {
  const value = item.label.replace(/ /g, '_').toLowerCase();
  return { label: item.label, value, visibilityRule: ProcessVisibilityRule.REPORT };
};

export const PROCESS_OPTIONS: ProcessOption[] = [
  ...ACCOUNTING_SECTION['new'].map(toProcessOption),
  { label: 'Financial Statements', value: 'financial_statements', visibilityRule: ProcessVisibilityRule.REPORT },
  { label: 'Projected JC Report', value: 'projected_jc_report', visibilityRule: ProcessVisibilityRule.REPORT },
  { label: 'Contract Progress Worksheet', value: 'contract_progress_worksheet', visibilityRule: ProcessVisibilityRule.REPORT },
  { label: 'Subcontract Payment Worksheet', value: 'subcontract_payment_worksheet', visibilityRule: ProcessVisibilityRule.REPORT },
  { label: 'User Request', value: 'user_request', visibilityRule: ProcessVisibilityRule.ADMIN },
].sort((a, b) => a.label.localeCompare(b.label));

export const PENDING_ACTION_PROCESS_OPTIONS: ProcessOption[] = [
  { label: 'New PR Timesheet', value: 'new_pr_timesheet', visibilityRule: ProcessVisibilityRule.REPORT },
  { label: 'Bonus', value: 'bonus', visibilityRule: ProcessVisibilityRule.REPORT },
  { label: 'Petty Cash Reimbursement', value: 'petty_cash_reimbursement', visibilityRule: ProcessVisibilityRule.REPORT },
  { label: 'Contract Progress Worksheet', value: 'contract_progress_worksheet', visibilityRule: ProcessVisibilityRule.REPORT },
  { label: 'Subcontract Payment Worksheet', value: 'subcontract_payment_worksheet', visibilityRule: ProcessVisibilityRule.REPORT },
  { label: 'Projected JC Report', value: 'projected_jc_report', visibilityRule: ProcessVisibilityRule.REPORT },
  { label: 'Create Payment Batch', value: 'create_payment_batch', visibilityRule: ProcessVisibilityRule.REPORT },
  { label: 'AP Transaction Entry', value: 'ap_transaction_entry', visibilityRule: ProcessVisibilityRule.REPORT },
].sort((a, b) => a.label.localeCompare(b.label));

export const reportsPerSection: Record<string, string[]> = {
  'cash management': getSectionReports('cash management'),
  'accounts payables': [
    ...getSectionReports('accounts payables'),
    'create_payment_batch',
  ],
  'accounts receivables': getSectionReports('accounts receivables'),
  'contract billings': getSectionReports('contract billings'),
  'jobs & contracts': getSectionReports('jobs & contracts'),
  payroll: getSectionReports('payroll'),
  subcontracts: getSectionReports('subcontracts'),
  'financial statements': getSectionReports('financial statements'),
  new: [
    ...getSectionReports('new'),
    'contract_progress_worksheet',
    'subcontract_payment_worksheet',
  ],
  policy: getSectionReports('policy', 'insurance'),
  claim: getSectionReports('claim', 'insurance'),
  insurance_new: ['claim', 'policy'],
};
